import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Eye, X, Calendar } from 'lucide-react';
import { articlesService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function AdminArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', category: '', is_published: false
  });

  useEffect(() => { loadArticles(); }, []);

  const loadArticles = async () => {
    try {
      const response = await articlesService.getAll({ published: 'false' });
      setArticles(response.data.articles || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title, excerpt: article.excerpt || '',
        content: article.content || '', category: article.category || '',
        is_published: article.is_published
      });
    } else {
      setEditingArticle(null);
      setFormData({ title: '', excerpt: '', content: '', category: '', is_published: false });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await articlesService.update(editingArticle.id, formData);
        toast.success('Article mis à jour');
      } else {
        await articlesService.create(formData);
        toast.success('Article créé');
      }
      setShowModal(false);
      loadArticles();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet article ?')) return;
    try {
      await articlesService.delete(id);
      toast.success('Article supprimé');
      loadArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><LoadingSpinner size="lg" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Articles</h1>
          <p className="text-dark-500">Gérez les articles du blog</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />Nouvel article
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr><th>Article</th><th>Catégorie</th><th>Vues</th><th>Statut</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id}>
                  <td>
                    <div className="font-medium text-dark-900">{article.title}</div>
                    <div className="text-sm text-dark-500">
                      {article.published_at && format(new Date(article.published_at), 'd MMM yyyy', { locale: fr })}
                    </div>
                  </td>
                  <td><span className="badge bg-dark-100 text-dark-600">{article.category || '-'}</span></td>
                  <td>{article.views_count}</td>
                  <td>
                    <span className={`badge ${article.is_published ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}>
                      {article.is_published ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openModal(article)} className="p-2 hover:bg-dark-100 rounded-lg text-dark-500 hover:text-primary-600">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(article.id)} className="p-2 hover:bg-red-50 rounded-lg text-dark-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-dark-100 flex items-center justify-between">
              <h2 className="text-xl font-semibold">{editingArticle ? 'Modifier' : 'Nouvel'} article</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="label">Titre *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} className="input" required />
              </div>
              <div>
                <label className="label">Catégorie</label>
                <input type="text" value={formData.category} onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} className="input" placeholder="Ex: ISO 20022, WealthTech" />
              </div>
              <div>
                <label className="label">Extrait</label>
                <textarea value={formData.excerpt} onChange={(e) => setFormData(p => ({ ...p, excerpt: e.target.value }))} className="input" rows={2} />
              </div>
              <div>
                <label className="label">Contenu *</label>
                <textarea value={formData.content} onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))} className="input" rows={10} required />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_published" checked={formData.is_published} onChange={(e) => setFormData(p => ({ ...p, is_published: e.target.checked }))} className="w-4 h-4 rounded" />
                <label htmlFor="is_published">Publier maintenant</label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">Annuler</button>
                <button type="submit" className="btn-primary">{editingArticle ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default AdminArticlesPage;
